import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, 
  CheckCircle2, 
  Clock,
  PenTool,
  Pencil,
  Eye,
  Trash2,
  Loader2,
} from 'lucide-react';
import { 
  formatDateTime,
  getRoleLabel 
} from '../../lib/utils';
import { signaturesApi, ctaFinalApprovalApi } from '../../lib/api';
import { isSignaturesTabStatus } from '../../lib/signaturesTabStatuses';
import { toast } from 'sonner';

export function SignaturesTab({ 
  request, 
  signatures, 
  signatureStatus, 
  setSignatureStatus,
  isAdmin, 
  isCoordinator, 
  isChairman, 
  isExecDirector,
  isCRD,
  onEditSignatories,
  onPreviewSignedDoc,
  onGoToSigningPage,
  onRefresh,
}) {
  const navigate = useNavigate();

  // iter167 Issue 11 — when a row is deleted or re-assigned, the tab needs
  // to pull the latest signature-status. Use the parent-provided callback
  // when available; otherwise fall back to a local hard reload of the
  // signature-status endpoint.
  const refreshSignatures = async () => {
    if (typeof onRefresh === 'function') {
      try { await onRefresh(); return; } catch (_e) { /* fall through */ }
    }
    try {
      const res = await signaturesApi.getStatus(request.id);
      setSignatureStatus?.(res.data);
    } catch (_e) {
      // best-effort — silent fail
    }
  };

  // iter167 Issue 23 — Single source of truth (lib/signaturesTabStatuses.js).
  // The parent RequestDetails.js uses the SAME helper to gate the tab strip
  // entry — guarantees the tab strip and the rendered content can never
  // disagree (previous bug: PENDING_CUSTOM_REVIEW / FT_PENDING_FINANCE were
  // missing from this child's array, producing a blank tab body).
  if (!request || !isSignaturesTabStatus(request.type, request.status)) {
    return null;
  }

  // iter140 — only Admin / Chairman / CRD can edit signatories on an in-flight doc.
  const canEditSignatories = (typeof isAdmin === 'function' ? isAdmin() : isAdmin)
    || (typeof isChairman === 'function' ? isChairman() : isChairman)
    || (typeof isCRD === 'function' ? isCRD() : !!isCRD);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              E-Signatures
            </CardTitle>
            <CardDescription>
              {request.status === 'EXECUTED'
                ? 'All signatures have been collected and the request has been executed'
                : request.status === 'READY_TO_EXECUTE'
                  ? 'All signatures have been collected — awaiting Execute action'
                  : (request.status === 'PENDING_DIRECTOR_REVIEW' || request.status === 'PENDING_EXEC_DIRECTOR_REVIEW')
                    ? 'E-Signature configuration is set — signing begins after Director and Executive Director approve'
                    : 'Each document requires signatures from assigned signatories'
              }
            </CardDescription>
          </div>
          {/* iter146 — top-level "Go to Signing Page" now shows all signatures, gated
              to admin / chairman / director / exec director / coordinator / finance only. */}
          {(() => {
            const canSeeAllSignatures = (typeof isAdmin === 'function' ? isAdmin() : isAdmin)
              || (typeof isChairman === 'function' ? isChairman() : isChairman)
              || (typeof isCRD === 'function' ? isCRD() : !!isCRD)
              || (typeof isCoordinator === 'function' ? isCoordinator() : isCoordinator)
              || (typeof isExecDirector === 'function' ? isExecDirector() : isExecDirector);
            // Visible from PENDING_SIGNATURES onward (covers EXECUTED too so they can
            // re-inspect prior rounds' signing pages).
            const visibleStatuses = ['PENDING_SIGNATURES', 'READY_TO_EXECUTE', 'EXECUTED'];
            if (!canSeeAllSignatures || !visibleStatuses.includes(request.status)) return null;
            return (
              <Button
                onClick={() => navigate(`/requests/${request.id}/sign`)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="signatures-tab-go-to-signing-page-btn"
              >
                <PenTool className="w-4 h-4 mr-2" />
                Go to Signing Page
              </Button>
            );
          })()}
        </div>
      </CardHeader>
      <CardContent>
        {/* Per-Document Signature Status */}
        {signatureStatus?.document_signature_status?.length > 0 ? (
          <div className="space-y-6">
            {signatureStatus.document_signature_status.map((docStatus) => (
              <div 
                key={docStatus.document_id}
                className={`p-4 rounded-lg border ${
                  docStatus.all_signed 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                {/* Document Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText className={`w-5 h-5 ${docStatus.all_signed ? 'text-emerald-600' : 'text-slate-500'}`} />
                    <div>
                      <p className="font-medium text-slate-900">{docStatus.document_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {docStatus.document_type}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {docStatus.signatures?.filter(s => s.status === 'SIGNED').length || 0}/{docStatus.signatures?.length || 0} signatures
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* iter141 — header-right action cluster:
                      • PENDING docs (any signature still pending) → "Edit Signatories" button
                        (Admin / Chairman / CRD only)
                      • FULLY SIGNED docs → "Preview Signed Doc" (opens modal) + "Go to Signing Page" */}
                  {!docStatus.all_signed && canEditSignatories && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`edit-signatories-${docStatus.document_id}`}
                      onClick={() => onEditSignatories?.(docStatus.document_id)}
                      className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      Edit Signatories
                    </Button>
                  )}
                  {docStatus.all_signed && docStatus.signed_pdf_id && (
                    <div className="flex items-center gap-2">
                      {/* iter141 — completed doc card. Edit Signatories + Refresh dropped
                          per user. "Preview Signed Doc" opens the unified preview modal,
                          "Go to Signing Page" jumps to the public signing-style page. */}
                      <Button 
                        variant="outline" 
                        size="sm"
                        data-testid={`preview-signed-${docStatus.document_id}`}
                        onClick={() => onPreviewSignedDoc?.(docStatus.signed_pdf_id)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview Signed Doc
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-500 text-blue-700 hover:bg-blue-50"
                        data-testid={`go-to-signing-page-${docStatus.document_id}`}
                        onClick={() => onGoToSigningPage?.(docStatus.document_id)}
                      >
                        <PenTool className="w-4 h-4 mr-2" />
                        Go to Signing Page
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Signatures for this document */}
                <div className="space-y-2 pl-8">
                  {docStatus.signatures?.map((sig) => (
                    <SignatureRow
                      key={sig.id}
                      signature={sig}
                      requestId={request.id}
                      canManage={canEditSignatories && (isAdmin || isChairman)}
                      onAfterChange={refreshSignatures}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : signatures.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No signatures configured for this request yet
          </div>
        ) : (
          /* Legacy: Show flat signature list if no per-document status */
          <div className="space-y-4">
            {signatures.map((sig) => (
              <LegacySignatureRow key={sig.id} signature={sig} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Sub-component for individual signature row.
// iter167 Issue 11 — Admin / Chairman get inline edit (re-assign) + delete
// icons next to PENDING rows. Signed rows expose a single trash icon that
// requires an extra confirm step (per user choice: full power to clean up
// duplicate / garbage signed data).
function SignatureRow({ signature: sig, requestId, canManage, onAfterChange }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(sig.user_id);

  const openEdit = async () => {
    setEditing(true);
    if (eligibleUsers.length === 0) {
      try {
        const res = await ctaFinalApprovalApi.getEligibleSignatoryUsers(requestId);
        // iter167 — Endpoint returns `{involved, others}` (each item shaped
        // {user_id, user_name, user_role, email}). Flatten + normalise so
        // the Select renderer below can treat them as one list.
        const raw = res.data || {};
        const merged = [...(raw.involved || []), ...(raw.others || [])];
        const seen = new Set();
        const normalised = [];
        for (const u of merged) {
          const id = u.id || u.user_id;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          normalised.push({
            id,
            name: u.name || u.user_name || u.email || id,
            role: u.role || u.user_role || '',
          });
        }
        setEligibleUsers(normalised);
      } catch (_e) {
        // best-effort — fall back to current signatory only
      }
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await signaturesApi.deleteSignature(requestId, sig.id);
      toast.success('Signature removed');
      setConfirmDelete(false);
      await onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete signature');
    } finally {
      setBusy(false);
    }
  };

  const doSaveEdit = async () => {
    if (!selectedUserId || selectedUserId === sig.user_id) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await signaturesApi.editSignature(requestId, sig.id, { user_id: selectedUserId });
      toast.success('Signatory updated');
      setEditing(false);
      await onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update signatory');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded border ${
        sig.status === 'SIGNED' 
          ? 'bg-green-50/50 border-green-100' 
          : 'bg-white border-slate-100'
      }`}
      data-testid={`signature-row-${sig.id}`}
    >
      <div className="flex items-center gap-3">
        {sig.status === 'SIGNED' ? (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        ) : (
          <Clock className="w-5 h-5 text-amber-500" />
        )}
        <div>
          <p className="text-sm font-medium text-slate-900">{sig.user_name}</p>
          <p className="text-xs text-slate-500">{getRoleLabel(sig.signature_role)}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          {sig.status === 'SIGNED' ? (
            <div>
              <p className="text-sm font-serif italic text-green-700">{sig.typed_name}</p>
              {sig.signed_at && (
                <p className="text-xs text-slate-500">{formatDateTime(sig.signed_at)}</p>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
              Pending
            </Badge>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1">
            {sig.status !== 'SIGNED' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-500 hover:text-blue-600"
                onClick={openEdit}
                data-testid={`edit-signature-${sig.id}`}
                title="Re-assign signatory"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-red-600"
              onClick={() => setConfirmDelete(true)}
              data-testid={`delete-signature-${sig.id}`}
              title={sig.status === 'SIGNED' ? 'Delete signed signature' : 'Delete pending signature'}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete this {sig.status === 'SIGNED' ? 'signed' : 'pending'} signature?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {sig.status === 'SIGNED' ? (
                <>
                  This will permanently remove the signed signature by <strong>{sig.user_name}</strong>.
                  The action is audit-logged. The original signed PDF on disk is NOT deleted.
                </>
              ) : (
                <>
                  This will remove <strong>{sig.user_name}</strong>&rsquo;s pending slot on this document.
                  Signing order will be re-numbered automatically.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              className="bg-red-600 hover:bg-red-700"
              disabled={busy}
              data-testid={`confirm-delete-signature-${sig.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-assign dialog */}
      <Dialog open={editing} onOpenChange={(v) => !busy && setEditing(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Re-assign Signatory</DialogTitle>
            <DialogDescription>
              Replace <strong>{sig.user_name}</strong> ({getRoleLabel(sig.signature_role)})
              with a different user. The signing order is preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">New signatory</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger data-testid={`reassign-signatory-select-${sig.id}`}>
                <SelectValue placeholder="Pick a user" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {eligibleUsers.length === 0 && (
                  <SelectItem value={sig.user_id} disabled>{sig.user_name} (current)</SelectItem>
                )}
                {eligibleUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} — {getRoleLabel(u.role)}
                    {u.id === sig.user_id ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={doSaveEdit}
              disabled={busy || !selectedUserId || selectedUserId === sig.user_id}
              data-testid={`confirm-reassign-signature-${sig.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Legacy signature row for flat list view
function LegacySignatureRow({ signature: sig }) {
  return (
    <div 
      className={`flex items-center justify-between p-4 rounded-lg border ${
        sig.status === 'SIGNED' 
          ? 'bg-green-50 border-green-200' 
          : 'bg-slate-50 border-slate-200'
      }`}
      data-testid={`signature-${sig.id}`}
    >
      <div className="flex items-center gap-4">
        {sig.status === 'SIGNED' ? (
          <CheckCircle2 className="w-6 h-6 text-green-500" />
        ) : (
          <Clock className="w-6 h-6 text-amber-500" />
        )}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">{sig.user_name}</p>
            <Badge variant="outline" className="text-xs">
              {getRoleLabel(sig.signature_role)}
            </Badge>
          </div>
          {sig.status === 'SIGNED' && sig.signed_at && (
            <p className="text-sm text-slate-500 mt-1">
              Signed on {formatDateTime(sig.signed_at)}
            </p>
          )}
        </div>
      </div>
      <div>
        {sig.status === 'SIGNED' ? (
          <div className="text-right">
            <p className="text-lg font-serif italic text-green-700">
              {sig.typed_name}
            </p>
            <p className="text-xs text-slate-500">
              {getRoleLabel(sig.user_role)}
            </p>
          </div>
        ) : (
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            Awaiting Signature
          </Badge>
        )}
      </div>
    </div>
  );
}

export default SignaturesTab;
